// Lervis additions to Dawn. Loaded once from layout/theme.liquid.

// Variant pills on product cards (card 07). product-form.js re-enables the first [name=id]
// input on load, so sold-out options are disabled again once the form is ready. The
// card adds in place and shows "Tillagd" on its button, as in the mockup, instead of
// opening the cart drawer: a capturing listener on product-form takes the submit before
// the form's own handler, and the drawer and cart icon are refreshed without opening.
if (!customElements.get('variant-pills')) {
  customElements.define(
    'variant-pills',
    class VariantPills extends HTMLElement {
      connectedCallback() {
        setTimeout(() => {
          this.querySelectorAll('input[data-unavailable]').forEach((input) => {
            input.disabled = true;
          });
        });
        this.form = this.closest('form');
        this.closest('product-form')?.addEventListener('submit', (event) => this.add(event), true);
      }

      async add(event) {
        if (event.target !== this.form) return;
        event.preventDefault();
        event.stopPropagation();
        const button = this.form.querySelector('[type="submit"]');
        const label = button.querySelector('span');
        const checked = this.querySelector('input[name="id"]:checked');
        if (!checked || button.getAttribute('aria-disabled') === 'true') return;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        button.querySelector('.loading__spinner')?.classList.remove('hidden');
        try {
          const response = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: Number(checked.value), quantity: 1 }],
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });
          const state = await response.json();
          if (!response.ok || state.status) throw new Error(state.description || state.message);
          this.refreshCart(state.sections);
          if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'variant-pills', cartData: state });
          this.flash(button, label, `\u2713 ${button.dataset.addedText || ''}`.trim(), 'is-added');
        } catch (e) {
          this.flash(button, label, e.message || window.cartStrings.error, 'is-error');
        } finally {
          button.classList.remove('loading');
          button.querySelector('.loading__spinner')?.classList.add('hidden');
          button.removeAttribute('aria-disabled');
        }
      }

      refreshCart(sections) {
        if (!sections) return;
        const parse = (html, selector) => new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        const bubble = document.getElementById('cart-icon-bubble');
        const freshBubble = sections['cart-icon-bubble'] && parse(sections['cart-icon-bubble'], '.shopify-section');
        if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;
        const drawer = document.querySelector('cart-drawer');
        const current = document.getElementById('CartDrawer');
        const fresh = sections['cart-drawer'] && parse(sections['cart-drawer'], '#CartDrawer');
        if (drawer && current && fresh) {
          current.innerHTML = fresh.innerHTML;
          drawer.classList.remove('is-empty');
        }
      }

      flash(button, label, text, className) {
        clearTimeout(this.flashTimer);
        if (!this.labelText) this.labelText = label.textContent;
        label.textContent = text;
        button.classList.add(className);
        this.flashTimer = setTimeout(() => {
          label.textContent = this.labelText;
          button.classList.remove(className);
        }, 3000);
      }
    }
  );
}

// "Komplettera din order" in the cart drawer (card 08). The buttons sit inside the
// cart form, so they add through the Ajax API and re-render the drawer from the
// returned sections instead of submitting a product form.
if (!customElements.get('cart-upsell')) {
  customElements.define(
    'cart-upsell',
    class CartUpsell extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (event) => {
          const button = event.target.closest('button[data-variant-id]');
          if (button) this.add(button);
        });
      }

      async add(button) {
        const cartDrawer = document.querySelector('cart-drawer');
        const error = this.querySelector('.cart-upsell__error');
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        error.hidden = true;
        try {
          const response = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });
          const state = await response.json();
          if (!response.ok || state.status) throw new Error(state.description || state.message);
          if (cartDrawer) {
            cartDrawer.renderContents({ ...state, id: state.items[0].product_id });
          } else {
            window.location.reload();
          }
          if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', cartData: state });
        } catch (e) {
          error.textContent = e.message || window.cartStrings.error;
          error.hidden = false;
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
        }
      }
    }
  );
}

// Level filter on the course list (card 10): shows the courses whose data-level
// matches the pressed chip; the first chip (no level) shows all.
if (!customElements.get('course-filter')) {
  customElements.define(
    'course-filter',
    class CourseFilter extends HTMLElement {
      connectedCallback() {
        this.list = this.parentElement.querySelector('.course-list__grid');
        this.addEventListener('click', (event) => {
          const chip = event.target.closest('button[data-level]');
          if (!chip) return;
          this.querySelectorAll('button[data-level]').forEach((button) =>
            button.setAttribute('aria-pressed', button === chip)
          );
          this.list.querySelectorAll('.course-card').forEach((card) => {
            card.hidden = chip.dataset.level !== '' && card.dataset.level !== chip.dataset.level;
          });
        });
      }
    }
  );
}

// "Komplettera din glasyr" on the product page (card 05): keeps the total and the
// button in step with the ticked rows and adds them to the cart in one request.
if (!customElements.get('product-complete')) {
  customElements.define(
    'product-complete',
    class ProductComplete extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.product-complete__add');
        this.addEventListener('change', (event) => {
          const select = event.target.closest('.product-complete__select');
          if (select) {
            const check = select.closest('.product-complete__item').querySelector('.product-complete__check');
            const option = select.selectedOptions[0];
            check.value = option.value;
            check.dataset.price = option.dataset.price;
            check.checked = true;
          }
          this.update();
        });
        this.button.addEventListener('click', () => this.add());
      }

      checked() {
        return [...this.querySelectorAll('.product-complete__check:checked')];
      }

      update() {
        const checked = this.checked();
        const total = checked.reduce((sum, check) => sum + Number(check.dataset.price), 0);
        this.querySelector('[data-total]').textContent = this.formatMoney(total);
        this.button.disabled = checked.length === 0;
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '{{amount}}';
        const number = (decimals, thousands, decimal) => {
          const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
          return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
        };
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          switch (key) {
            case 'amount_no_decimals':
              return number(0, ',', '.');
            case 'amount_with_comma_separator':
              return number(2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return number(0, '.', ',');
            case 'amount_with_space_separator':
              return number(2, ' ', ',');
            case 'amount_no_decimals_with_space_separator':
              return number(0, ' ', ',');
            default:
              return number(2, ',', '.');
          }
        });
      }

      async add() {
        const cartDrawer = document.querySelector('cart-drawer');
        const error = this.querySelector('.product-complete__error');
        const items = this.checked().map((check) => ({ id: Number(check.value), quantity: 1 }));
        if (!items.length) return;
        this.button.disabled = true;
        this.button.classList.add('loading');
        error.hidden = true;
        try {
          const response = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items,
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });
          const state = await response.json();
          if (!response.ok || state.status) throw new Error(state.description || state.message);
          this.querySelectorAll('.product-complete__check').forEach((check) => (check.checked = false));
          this.update();
          if (cartDrawer) {
            cartDrawer.renderContents({ ...state, id: state.items[0].product_id });
          } else {
            window.location.reload();
          }
          if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-complete', cartData: state });
        } catch (e) {
          error.textContent = e.message || window.cartStrings.error;
          error.hidden = false;
          this.update();
        } finally {
          this.button.classList.remove('loading');
        }
      }
    }
  );
}

// Header (card 01): the info row and the mega panel start where the product menu's
// text starts, as in the mockup. That depends on the logo width, so it is measured.
(() => {
  const align = () => {
    const header = document.querySelector('.header');
    const first = document.querySelector('.header__inline-menu .header__menu-item span');
    if (!header || !first) return;
    const offset = first.getBoundingClientRect().left - header.getBoundingClientRect().left;
    document.documentElement.style.setProperty('--lervis-nav-left', `${Math.round(offset)}px`);
  };
  align();
  window.addEventListener('resize', align);
  if (document.fonts) document.fonts.ready.then(align);
})();
